import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Portal } from './types';
import { getLoginKitMessages } from './i18n';
import type { Locale } from './i18n';
import { getLegalDocs } from './legalContent';
import type { LegalDocKind } from './legalContent';

export interface LegalModalProps {
  portal: Portal;
  /** Idioma da interface (pt-BR, en, es) — controla apenas o chrome do modal. */
  locale?: Locale;
  /** Documento aberto ao montar. Default: `terms`. */
  initial?: LegalDocKind;
  onClose: () => void;
}

/**
 * Modal de leitura dos documentos legais (Termos de Uso + Política de
 * Privacidade), acessível a partir do rodapé de qualquer portal. Alterna entre
 * os dois documentos por abas, rola internamente e fecha no Esc e no clique do
 * scrim. COMPARTILHADO por todos os portais; reusa as classes do modal do login.
 */
export function LegalModal({ portal, locale = 'pt-BR', initial = 'terms', onClose }: LegalModalProps) {
  const t = getLoginKitMessages(locale);
  const docs = getLegalDocs(portal.name);
  const [active, setActive] = useState<LegalDocKind>(initial);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Ao trocar de aba, volta ao topo do documento.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [active]);

  const doc = docs[active];
  const modalStyle = { '--accent': portal.accent, '--accent-soft': portal.accentSoft } as CSSProperties;
  const tabs: LegalDocKind[] = ['terms', 'privacy'];

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal modal--legal"
        onClick={(e) => e.stopPropagation()}
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-label={doc.title}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label={t.modal.close}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="legal-tabs" role="tablist" aria-label={doc.title}>
          {tabs.map((kind) => (
            <button
              key={kind}
              type="button"
              role="tab"
              id={`legal-tab-${kind}`}
              aria-selected={active === kind}
              aria-controls="legal-panel"
              className={`legal-tab ${active === kind ? 'is-active' : ''}`}
              onClick={() => setActive(kind)}
            >
              {docs[kind].title}
            </button>
          ))}
        </div>

        <div
          className="legal-scroll"
          ref={scrollRef}
          id="legal-panel"
          role="tabpanel"
          aria-labelledby={`legal-tab-${active}`}
          tabIndex={0}
        >
          <article className="legal-doc" key={active}>
            <div className="modal-eyebrow">{portal.name}</div>
            <h2 className="modal-title">{doc.title}</h2>
            <p className="legal-lead">{doc.lead}</p>
            {doc.blocks.map((block, i) => {
              if (block.kind === 'heading') {
                return (
                  <h3 className="legal-heading" key={i}>
                    {block.text}
                  </h3>
                );
              }
              if (block.kind === 'list') {
                return (
                  <ul className="legal-list" key={i}>
                    {block.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                );
              }
              return (
                <p className="legal-paragraph" key={i}>
                  {block.text}
                </p>
              );
            })}
          </article>
        </div>
      </div>
    </div>
  );
}
